import { Typography } from '@mui/material'
import { getOptjobAsObject, OptJobType, useLatestOptJobStatus } from 'api/optJobs/optJobs.api'
import { useSystem } from 'api/systems/systems.api'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from 'ui/atoms/Icon/Icon'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import Datetime from 'utils/datetime/datetime'

type ProductionPlanOutOfDateProps = {
  optJobType: OptJobType
}

export default function ProductionPlanOutOfDate({ optJobType }: ProductionPlanOutOfDateProps): ReactElement | null {
  const { t } = useTranslation()
  const { systemId } = useAuth()
  const { data: activeSystem } = useSystem({ id: systemId })

  const { data: latestFinishedOptJob } = useLatestOptJobStatus({ type: optJobType, status: 'Finished' })
  const firstLatestFinishedOptJob = getOptjobAsObject(latestFinishedOptJob)

  const hoursSinceLatestOptJob = useMemo(() => {
    if (!firstLatestFinishedOptJob?.updated_at) {
      return 0
    }
    return Datetime.getNrOfHoursBetween(firstLatestFinishedOptJob.updated_at as ISODateTime, Datetime.getISONow()) - 1
  }, [firstLatestFinishedOptJob?.updated_at])

  const hasUnusedSettings = useMemo(() => {
    return (
      activeSystem?.primary_opt_model?.has_unused_settings || activeSystem?.primary_digital_twin?.has_unused_settings
    )
  }, [activeSystem?.primary_opt_model?.has_unused_settings, activeSystem?.primary_digital_twin?.has_unused_settings])

  const isUrgent = hoursSinceLatestOptJob >= 12

  if (hasUnusedSettings) {
    // Show unused settings even if hoursSinceLatestOptJob >= 1, if there are unused settings
    return (
      <InfoBox
        title={t(`Start a new optimization to use the latest settings in the optimization`)}
        message={t(`New settings are available`)}
        isUrgent={isUrgent}
      />
    )
  }

  if (hoursSinceLatestOptJob >= 1) {
    return (
      <InfoBox
        title={t(
          `Each hour new forecast and measurement values are available. Start a new optimization to use the latest input data in the optimization`
        )}
        message={t(`New input data is available`)}
        isUrgent={isUrgent}
      />
    )
  }

  return null
}

type InfoBoxProps = {
  title: string
  message: string
  isUrgent: boolean
}

function InfoBox({ title, message, isUrgent }: InfoBoxProps): ReactElement {
  return (
    <div className={`rounded p-1 text-center ${isUrgent ? 'bg-danger-4' : 'bg-warning-4'}`}>
      <Tooltip title={title} placement="bottom" arrow display="block">
        <Typography
          variant="subtitle2"
          className="text-white flex items-center justify-center gap-1 xl:whitespace-nowrap"
        >
          {message}
          <Icon icon="fas fa-info-circle" size="medium" aria-label="Info" />
        </Typography>
      </Tooltip>
    </div>
  )
}
